<template>
  <v-row class="tester-rooms-view">
    <v-col>
      <v-expansion-panels class="roomsList" v-model="roomPanelOpen" tile>
        <v-expansion-panel
          class="overflow-y-scroll pt-0 mt-0"
          v-for="room in selectedFloorRooms"
          :key="room.room_id"
        >
          <v-expansion-panel-header
            class="pr-0 panel-header"
            disable-icon-rotate
            :expand-icon="
              roomPanelOpen === selectedFloorRooms.indexOf(room)
                ? '$iconChevronDown'
                : '$iconChevronLeft'
            "
          >
            <v-row class="d-flex justify-space-between pr-8 align-baseline"
              ><span class="subtitle-1">{{ room.name }}</span>

              <span class="overline">
                {{ $t("tests.summary", [testsDone(room), totalTests(room)]) }}
              </span>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pa-0 ma-0">
            <v-list-item-group>
              <div
                v-for="(test, index) of room.illumination_tests.concat(
                  room.functional_tests.filter(
                    (test) =>
                      test.daylight_control_enabled ||
                      test.motion_detectors_enabled
                  )
                )"
                :key="test.illumination_test_id || test.functional_test_id"
              >
                <v-divider :class="index === 0 ? '' : 'ml-6'"></v-divider>
                <v-list-item
                  @click="
                    selectTest(
                      room,
                      test.illumination_test_id || test.functional_test_id
                    )
                  "
                  outlined
                  two-line
                  class="ml-6 pr-2"
                >
                  <v-list-item-content>
                    <v-list-item-title class="mb-3">
                      <v-row
                        class="
                          d-flex
                          justify-space-between
                          align-baseline
                          flex-nowrap
                          mr-0
                          pr-0
                          ml-0
                        "
                      >
                        <span class="text-truncate">{{ test.name }}</span>
                        <span
                          class="overline pl-1"
                          :class="
                            test.illumination_test_results
                              ? test.illumination_test_results.length ===
                                room.measurement_points.filter(
                                  (point) => point.is_active
                                ).length
                                ? 'text-green'
                                : 'text-red'
                              : isFunctionalTestDone(test)
                              ? 'text-green'
                              : 'text-red'
                          "
                        >
                          {{
                            test.illumination_test_results
                              ? test.illumination_test_results.length ===
                                room.measurement_points.filter(
                                  (point) => point.is_active
                                ).length
                                ? $t("tests.done")
                                : $t("tests.not_done")
                              : isFunctionalTestDone(test)
                              ? $t("tests.done")
                              : $t("tests.not_done")
                          }}
                        </span>
                      </v-row>
                    </v-list-item-title>
                    <v-list-item-subtitle
                      class="tester-rooms-view__requirements"
                    >
                      {{
                        !!test.test_requirements
                          ? `${test.test_requirements}`
                          : " - "
                      }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </div>
            </v-list-item-group>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-col>
    <guidance-button page="testing.rooms_list" />
  </v-row>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { eventBus } from "@/components/EventBus";
import GuidanceButton from "@/components/guidance/GuidanceButton";

export default {
  name: "tester-rooms-view",
  components: { GuidanceButton },
  data: () => ({
    roomPanelOpen: 0,
  }),
  computed: {
    ...mapState("projectStore", ["buildings", "project_id", "state"]),
    ...mapState("floorStore", ["floor_id"]),
    ...mapState("buildingStore", ["building_id"]),
    selectedFloorRooms() {
      if (!this.building_id || !this.floor_id) {
        return [];
      }
      const buildingIndex = this.buildings.findIndex(
        (building) => building.building_id === this.building_id
      );
      const floorIndex = this.buildings[buildingIndex].floors.findIndex(
        (floor) => floor.floor_id === this.floor_id
      );

      return this.buildings[buildingIndex].floors[floorIndex].rooms || [];
    },
  },
  methods: {
    ...mapMutations("roomStore", [
      "setRoomId",
      "setRoomName",
      "setRoomUse",
      "setRoomInfoComment",
      "setRoomLength",
      "setMeasurementHeight",
      "setMeasurementPointsComment",
      "setIsRoomClosed",
      "setRoomCorners",
      "setDistanceBetweenPointsX",
      "setDistanceBetweenPointsY",
      "setSelectedTestId",
      "setFreezeDistanceBetweenPoints",
      "setMeasurementPoints",
      "setIlluminationTests",
      "setFunctionalTests",
    ]),
    ...mapActions("projectStore", ["getProjectById"]),
    nrOfFunctionalTestsDone(room) {
      if (!room.functional_tests) {
        return 0;
      }

      return room.functional_tests.filter(
        (test) =>
          (test.daylight_control_enabled || test.motion_detectors_enabled) &&
          this.isFunctionalTestDone(test)
      ).length;
    },
    nrOfIlluminationTestsDone(room) {
      if (!room.illumination_tests) {
        return 0;
      }

      return room.illumination_tests.filter(
        (test) =>
          test.illumination_test_results.length ===
          room.measurement_points.filter((point) => point.is_active).length
      ).length;
    },
    selectTest(room, test_id) {
      this.setSelectedTestId(test_id);
      this.setRoomId(room.room_id);
      this.setRoomName(room.name);
      this.setRoomUse(room.room_type);
      this.setRoomInfoComment(room.room_info_comment);
      this.setMeasurementPointsComment(room.measurement_points_comment);
      this.setRoomLength(room.longer_size_length ?? 0);
      this.setMeasurementHeight(room.measurement_height);
      this.setDistanceBetweenPointsX(room.distance_between_points_x);
      this.setDistanceBetweenPointsY(room.distance_between_points_y);
      setTimeout(() => {
        this.setFreezeDistanceBetweenPoints(true);
        this.setIsRoomClosed(room.corners.length > 0);
      }, 1);
      const corners = room.corners.map((corner) => {
        return {
          ...corner,
        };
      });
      this.setRoomCorners(corners ?? []);
      const measurement_points = room.measurement_points.map((point) => {
        return {
          ...point,
        };
      });
      setTimeout(() => {
        this.setMeasurementPoints(measurement_points ?? []);
      }, 1);
      const illumination_tests = room.illumination_tests.map((test) => {
        return {
          ...test,
        };
      });
      this.setIlluminationTests(illumination_tests);
      const functional_tests = room.functional_tests.map((test) => {
        return {
          ...test,
        };
      });
      this.setFunctionalTests(functional_tests);
      eventBus.$emit("centerRoom");
    },
    isFunctionalTestDone(test) {
      const requiredResults =
        (test.daylight_control_enabled ? 1 : 0) +
        (test.daylight_zones_enabled ? 2 : 0) +
        (test.motion_detectors_enabled ? 3 : 0);
      const enoughResults =
        test.functional_test_results.length === requiredResults;
      const allResultsValid = test.functional_test_results.every(
        (result) => result.answer === true || result.answer === false
      );
      const actualNumberOfZonesSpecified =
        test.actual_number_of_zones !== null &&
        test.actual_number_of_zones !== undefined;
      return (
        enoughResults &&
        allResultsValid &&
        (actualNumberOfZonesSpecified || !test.daylight_zones_enabled)
      );
    },
    testsDone(room) {
      return (
        this.nrOfIlluminationTestsDone(room) +
        this.nrOfFunctionalTestsDone(room)
      );
    },
    totalTests(room) {
      return (
        room.illumination_tests.length +
        room.functional_tests.filter(
          (test) =>
            test.daylight_control_enabled || test.motion_detectors_enabled
        ).length
      );
    },
  },
  watch: {
    selectedFloorRooms() {
      setTimeout(() => {
        this.state === "in_testing" && this.getProjectById(this.project_id);
        this.roomPanelOpen = 0;
      }, 1);
    },
  },
};
</script>

<style lang="scss">
.tester-rooms-view {
  &__requirements {
    overflow: initial;
    white-space: initial;
  }

  .v-expansion-panel-header {
    min-height: 40px !important;
    max-height: 40px !important;
  }

  .v-expansion-panel::before {
    box-shadow: none;
  }
  .theme--dark.v-list-item--active:hover::before {
    opacity: 0.08 !important;
  }
  .theme--dark.v-list-item--active::before {
    opacity: 0 !important;
  }
}
</style>
